import { FilterModel, IServerSideGetRowsParams } from '@ag-grid-community/core';
import { Injectable, signal } from '@angular/core';
import { Apollo, ApolloBase, gql } from 'apollo-angular';
import { ImportsMonitoringChannelDrilldownImport } from 'exports/interfaces/imports-monitoring-channel.interface';
import { Observable, map, of } from 'rxjs';
import { GridService } from 'shared/interfaces/grid-service';
import { CachingService } from 'shared/services';

@Injectable({
    providedIn: 'root',
})
export class ImportsMonitoringChannelDrilldownService implements GridService {
    private apollo: ApolloBase;
    NAME = 'ImportsMonitoringChannelDrilldownService';

    constructor(
        private apolloProvider: Apollo,
        private cachingService: CachingService,
    ) {
        this.apollo = this.apolloProvider.use('distributionManagementAPI');
    }

    sourceId = signal<string | null>(null);

    convertToBoolean(value: any): boolean | undefined {
        if (value === undefined || value === null) return undefined;
        if (typeof value === 'boolean') return value;
        if (typeof value === 'string') {
            const lowerValue = value.toLowerCase();
            if (lowerValue === 'true') return true;
            if (lowerValue === 'false') return false;
        }
        return undefined;
    }

    public getRows(params: IServerSideGetRowsParams, pageNumber: number, pageSize: number): Observable<any> {
        const orderBy = params.request.sortModel[0]?.colId ?? 'name';
        const orderDirection = params.request.sortModel[0]?.sort?.toUpperCase() ?? 'ASC';

        const sourceId = this.sourceId();
        if (!sourceId) {
            return of({ data: [], totalCount: 0 });
        }

        return this.apollo
            .query({
                query: gql`
                    query getImportTrackingItems(
                        $listingName: String
                        $supplierName: String
                        $importDate: ExportImportDateEnum
                        $pageNumber: Int
                        $pageSize: Int
                        $orderBy: String
                        $orderDirection: OrderDirection
                        $importStatus: ImportMonitoringItemStatus
                        $importType: ImportItemType
                        $importTrackerId: Guid
                    ) {
                        importMonitoring {
                            searchImportMonitoringItems(
                                importMonitoringItemFilter: {
                                    itemName: $listingName
                                    supplierName: $supplierName
                                    importDate: $importDate
                                    importStatus: $importStatus
                                    importType: $importType
                                    importTrackerId: $importTrackerId
                                }
                                searchQuery: {
                                    pageNumber: $pageNumber
                                    pageSize: $pageSize
                                    orderBy: $orderBy
                                    orderDirection: $orderDirection
                                }
                            ) {
                                data {
                                    id
                                    importDataId
                                    itemId
                                    itemName
                                    importStatus
                                    importType
                                    importEntityType
                                    importDateOutput
                                    importDate
                                    importData {
                                        importTrackerId
                                        supplierId
                                        supplierName
                                        trackingId
                                    }
                                    listingImportItemDetails {
                                        featureGroupId
                                        featureGroupName
                                        details {
                                            id
                                            isSuccessful
                                            name
                                        }
                                    }
                                }
                                statusCode
                                totalCount
                            }
                        }
                    }
                `,
                variables: {
                    pageNumber,
                    pageSize,
                    listingName: (params.request.filterModel as FilterModel)['listingName']?.filter,
                    supplierName: (params.request.filterModel as FilterModel)['supplierName']?.filter,
                    importDate: (params.request.filterModel as FilterModel)['importDate']?.filter,
                    importStatus: (params.request.filterModel as FilterModel)['importStatus']?.filter,
                    importType: (params.request.filterModel as FilterModel)['importType']?.filter,
                    importTrackerId: sourceId,
                    orderBy,
                    orderDirection,
                },
                fetchPolicy: 'no-cache',
            })
            .pipe(
                map((response: any) => {
                    const returnValue = response.data.importMonitoring.searchImportMonitoringItems;
                    const importsData: ImportsMonitoringChannelDrilldownImport[] = returnValue.data.map((element: any) => {
                        const importData: ImportsMonitoringChannelDrilldownImport = {
                            id: element.id,
                            listingName: element.itemName,
                            listingId: element.itemId,
                            supplierName: element.importData.supplierName,
                            companyId: element.importData.supplierId,
                            status: element.importStatus,
                            importType: element.importType,
                            importDate: element.importDate,
                            itemDetails: element.listingImportItemDetails,
                            group: true,
                        };
                        return importData;
                    });
                    return { data: importsData, totalCount: returnValue.totalCount };
                }),
            );
    }
}
